import { useState, useEffect, useCallback } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { UserType } from "../../../constants";
import { getUserDataByAuth } from "../lib/userData";

/**
 * useAuth: Firebase Auth + users collection resolution.
 * Only considers the session valid if userData.type === UserType.client.
 * Exposes: user, userData (uid=clientId, organizationId), clientId, organizationId, isAuthenticated, isClient, isLoading, signOut.
 */
export function useAuth() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolveError, setResolveError] = useState(null);

  const resolveUser = useCallback(async (authUser) => {
    if (!authUser) {
      setUser(null);
      setUserData(null);
      setResolveError(null);
      return;
    }
    try {
      const data = await getUserDataByAuth(authUser.uid);
      if (data && data.type === UserType.client) {
        setUser(authUser);
        setUserData(data);
        setResolveError(null);
      } else {
        setUser(authUser);
        setUserData(null);
        setResolveError(data ? "not_client" : "no_user_doc");
      }
    } catch (err) {
      setUser(authUser);
      setUserData(null);
      setResolveError("resolve_failed");
    }
  }, []);

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async (authUser) => {
      setLoading(true);
      await resolveUser(authUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [resolveUser]);

  const signOut = useCallback(() => {
    firebase.auth().signOut();
    setUser(null);
    setUserData(null);
    setResolveError(null);
  }, []);

  const clientId = userData?.uid ?? null;
  const organizationId = userData?.organizationId ?? null;
  const isAuthenticated = !!user;
  const isClient = !!userData;

  return {
    user,
    userData,
    clientId,
    organizationId,
    isAuthenticated,
    isClient,
    isLoading: loading,
    resolveError,
    signOut,
  };
}
